import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MobileGenericLabelsTool from './MobileGenericLabelsTool';
import useSheet from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { canShowLabelsToolSelector } from '../labelToolSelectors';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { Label } from '../../../../../../../common/labels/labelTypes';
import LabelEditSheet from './LabelEditSheet';
import { selectUsableLabels } from '../../../../../../element/labels/labelSelectors';
import LabelDeleteSheet from './LabelDeleteSheet';
import useLabelsTool from '../hooks/useLabelsTool';
import useBecameTrueEffect from '../../../../../../utils/react/useBecameTrueEffect';
import { deleteLabel } from '../../../../../../../common/labels/labelActions';
import LabelActionsSheet from './LabelActionsSheet';
import { useLabelUsageData } from '../labelUsage/useLabelUsageData';
import { SheetId } from '../../../../../../mobile/structural/sheet/sheetTypes';

type MobileElementLabelsToolProps = {
    currentUserId: string;
    gridSize: number;
    name: string;
    selectedElements: Immutable.List<ImMNElement>;
};

/** Element-owned mobile label tool, including the existing global label management flow. */
const MobileElementLabelsTool = (props: MobileElementLabelsToolProps): ReactElement | null => {
    const { currentUserId, gridSize, name } = props;

    const dispatch = useDispatch();
    const canShowLabelsTool = useSelector(canShowLabelsToolSelector);
    const sheetProps = useSheet(SheetId.LabelsTool);
    const actionSheetProps = useSheet(SheetId.LabelActionsTool);
    const editSheetProps = useSheet(SheetId.LabelEditTool);
    const deleteSheetProps = useSheet(SheetId.LabelDeleteTool);
    const { isSheetOpen } = sheetProps;
    const controller = useLabelsTool({ currentUserId, isActive: isSheetOpen });
    const [currentlyEditingLabelId, setCurrentlyEditingLabelId] = useState<string | null>(null);
    const [editingLabelSnapshot, setEditingLabelSnapshot] = useState<Label | null>(null);
    const labelMap = useSelector(selectUsableLabels);
    const currentlyEditingLabel = labelMap?.[currentlyEditingLabelId || ''] || editingLabelSnapshot;
    const isLabelFlowOpen = sheetProps.isSheetOpen || currentlyEditingLabelId;
    const usageDataState = useLabelUsageData(currentlyEditingLabelId);

    const handleClickActions = (label: Label) => {
        setCurrentlyEditingLabelId(label._id);
        setEditingLabelSnapshot(label);
        actionSheetProps.dispatchOpenSheet();
    };

    const handleOpenEditSheet = () => {
        editSheetProps.dispatchOpenSheet();
    };

    useEffect(() => {
        if (!sheetProps.isSheetOpen || !currentlyEditingLabelId) return;

        setCurrentlyEditingLabelId(null);
        setEditingLabelSnapshot(null);
    }, [currentlyEditingLabelId, sheetProps.isSheetOpen]);

    useBecameTrueEffect(() => {
        controller.setSearchString('');
    }, !isLabelFlowOpen);

    const handleOpenDeleteSheet = () => {
        deleteSheetProps.dispatchOpenSheet();
    };

    const handleDeleteLabel = () => {
        if (currentlyEditingLabelId) dispatch(deleteLabel(currentlyEditingLabelId));

        deleteSheetProps.dispatchCloseSheet({ popToSheetId: SheetId.LabelsTool });
    };

    if (!canShowLabelsTool) return null;

    return (
        <>
            <MobileGenericLabelsTool
                gridSize={gridSize}
                name={name}
                sheetProps={sheetProps}
                controller={controller}
                handleClickEdit={handleClickActions}
            />
            {currentlyEditingLabel && (
                <>
                    <LabelActionsSheet
                        label={currentlyEditingLabel}
                        onEditClick={handleOpenEditSheet}
                        onDeleteClick={handleOpenDeleteSheet}
                        sheetProps={actionSheetProps}
                        usageDataState={usageDataState}
                    />
                    <LabelEditSheet label={currentlyEditingLabel} sheetProps={editSheetProps} />
                    <LabelDeleteSheet onDelete={handleDeleteLabel} sheetProps={deleteSheetProps} />
                </>
            )}
        </>
    );
};

export default MobileElementLabelsTool;
